import { useState, useEffect } from 'react'
import { Alert, Button, Card, Container, Form, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import {
  fetchUsers,
  createUser,
  updateUser,
  approveUser,
  deleteUser,
  impersonateUser,
  fetchDivisions,
  fetchSubdivisions,
  fetchBusinessEntities,
  type AppUserDto,
  type AppUserCreateRequest,
  type DivisionDto,
  type SubdivisionDto,
  type BusinessEntityDto,
} from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'

const BUSINESS_LEVELS = ['MasterAdmin', 'Division', 'Subdivision', 'Business'] as const
const BUSINESS_LEVEL_LABELS: Record<string, string> = {
  MasterAdmin: 'Group',
  Division: 'Divisional',
  Subdivision: 'Regional',
  Business: 'Business Entity',
}
const FUNCTIONAL_ROLES = ['Author', 'Publisher', 'ComplianceChecker', 'UserManagement'] as const

export default function Users() {
  const { isAuthenticated, user: currentUser, startImpersonation } = useAuth()
  const navigate = useNavigate()
  const canImpersonate =
    currentUser?.businessLevel === 'MasterAdmin' ||
    (currentUser?.functionalRoles ?? []).some((r) => r.toLowerCase() === 'usermanagement')
  const [users, setUsers] = useState<AppUserDto[]>([])
  const [divisions, setDivisions] = useState<DivisionDto[]>([])
  const [subdivisions, setSubdivisions] = useState<SubdivisionDto[]>([])
  const [businessEntities, setBusinessEntities] = useState<BusinessEntityDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<Partial<AppUserCreateRequest>>({
    userId: '',
    displayName: '',
    email: '',
    businessLevel: 'Business',
    functionalRoles: [],
    businessEntityIds: [],
    password: '',
  })

  const load = () => {
    setError('')
    Promise.all([
      fetchUsers(),
      fetchDivisions(),
      fetchSubdivisions(),
      fetchBusinessEntities(),
    ])
      .then(([u, d, s, b]) => {
        setUsers(u)
        setDivisions(d)
        setSubdivisions(s)
        setBusinessEntities(b)
      })
      .catch((err) => setError(err.response?.data?.message ?? err.message ?? 'Failed to load'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (isAuthenticated) load()
  }, [isAuthenticated])

  const handleCreate = () => {
    setCreating(true)
    setEditingId(null)
    setForm({
      userId: '',
      displayName: '',
      email: '',
      businessLevel: 'Business',
      functionalRoles: [],
      businessEntityIds: [],
      password: '',
    })
  }

  const handleEdit = (u: AppUserDto) => {
    setEditingId(u.id)
    setCreating(false)
    setForm({
      userId: u.userId,
      displayName: u.displayName ?? '',
      email: u.email ?? '',
      role: u.role,
      businessLevel: u.businessLevel ?? 'Business',
      functionalRoles: u.functionalRoles ?? [],
      divisionId: u.divisionId,
      subdivisionId: u.subdivisionId,
      businessEntityIds: u.businessEntityIds ?? [],
      password: '',
    })
  }

  const handleSaveCreate = async () => {
    if (!form.userId?.trim()) {
      setError('User ID is required.')
      return
    }
    if (!form.password?.trim()) {
      setError('Password is required.')
      return
    }
    setError('')
    try {
      const password = (form.password ?? '').trim()
      await createUser({
        userId: form.userId!.trim(),
        displayName: form.displayName?.trim(),
        email: form.email?.trim(),
        role: form.role,
        businessLevel: form.businessLevel ?? 'Business',
        functionalRoles: form.functionalRoles ?? [],
        divisionId: form.divisionId,
        subdivisionId: form.subdivisionId,
        businessEntityIds: form.businessEntityIds?.length ? form.businessEntityIds : undefined,
        password,
      })
      setCreating(false)
      load()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Create failed.')
    }
  }

  const handleSaveEdit = async () => {
    if (!editingId) return
    setError('')
    try {
      await updateUser(editingId, {
        displayName: form.displayName?.trim(),
        email: form.email?.trim(),
        businessLevel: form.businessLevel,
        functionalRoles: form.functionalRoles,
        divisionId: form.businessLevel === 'MasterAdmin' || form.businessLevel === 'Division'
          ? (form.divisionId || null)
          : null,
        subdivisionId: form.subdivisionId,
        businessEntityIds: form.businessEntityIds,
        password: form.password?.trim() ? form.password : undefined,
      })
      setEditingId(null)
      load()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Update failed.')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this user?')) return
    setError('')
    try {
      await deleteUser(id)
      if (editingId === id) setEditingId(null)
      load()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Delete failed.')
    }
  }

  const handleApprove = async (id: string) => {
    setError('')
    try {
      await approveUser(id)
      if (editingId === id) setEditingId(null)
      load()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Approve failed.')
    }
  }

  const handleImpersonate = async (u: AppUserDto) => {
    if (!confirm(`View the app as ${u.displayName ?? u.userId}? You can return to your account from the banner at the top.`)) {
      return
    }
    setError('')
    try {
      const res = await impersonateUser(u.id)
      startImpersonation(res.token, {
        userId: res.user.userId,
        displayName: res.user.displayName,
        email: res.user.email,
        role: res.user.role,
        businessLevel: res.user.businessLevel,
        functionalRoles: res.user.functionalRoles,
        divisionId: res.user.divisionId,
        subdivisionId: res.user.subdivisionId,
        businessEntityIds: res.user.businessEntityIds?.map(String),
        isImpersonating: true,
        impersonatorUserId: res.user.impersonatorUserId,
        impersonatorDisplayName: res.user.impersonatorDisplayName,
        authMethod: 'local',
      })
      navigate('/', { replace: true })
    } catch (err: any) {
      const msg = err.response?.data?.error ?? err.response?.data ?? err.message ?? 'Impersonation failed.'
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg))
    }
  }

  const toggleBusinessEntity = (id: string) => {
    const list = form.businessEntityIds ?? []
    const set = new Set(list)
    if (set.has(id)) set.delete(id)
    else set.add(id)
    setForm({ ...form, businessEntityIds: Array.from(set) })
  }

  const toggleFunctionalRole = (role: string) => {
    const list = form.functionalRoles ?? []
    const set = new Set(list)
    if (set.has(role)) set.delete(role)
    else set.add(role)
    setForm({ ...form, functionalRoles: Array.from(set) })
  }

  if (!isAuthenticated) {
    return (
      <Container className="py-3">
        <Alert variant="info" className="mb-0">
          Sign in to manage users.
        </Alert>
      </Container>
    )
  }

  return (
    <Container className="py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 className="h3 mb-1">User management</h1>
          <p className="text-muted mb-0">Business level controls scope. Functional roles control capabilities.</p>
        </div>
        <div className="d-flex gap-2">
          {currentUser?.businessLevel === 'MasterAdmin' && (
            <Button type="button" variant="outline-secondary" as={Link as any} to="/admin/divisions">
              Division management
            </Button>
          )}
          <Button type="button" variant="primary" onClick={handleCreate}>
            Add user
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}

      {(creating || editingId) && (
        <Card className="mb-3">
          <Card.Body>
            <h2 className="h6 mb-3">{creating ? 'New user' : 'Edit user'}</h2>
            <Form className="row g-3">
              {creating && (
                <Form.Group className="col-md-4">
                  <Form.Label>User ID *</Form.Label>
                  <Form.Control
                    value={form.userId ?? ''}
                    onChange={(e) => setForm({ ...form, userId: e.target.value })}
                    placeholder="Unique id (e.g. email or oid)"
                  />
                </Form.Group>
              )}
              <Form.Group className="col-md-4">
                <Form.Label>Display name</Form.Label>
                <Form.Control
                  value={form.displayName ?? ''}
                  onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                  placeholder="Optional"
                />
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={form.email ?? ''}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Optional"
                />
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label>Password {creating ? '*' : '(leave blank to keep current)'}</Form.Label>
                <Form.Control
                  type="password"
                  value={form.password ?? ''}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={creating ? 'Set initial password' : 'Set new password'}
                />
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label>Business level</Form.Label>
                <Form.Select
                  value={form.businessLevel ?? 'Business'}
                  onChange={(e) => setForm({ ...form, businessLevel: e.target.value })}
                >
                  {BUSINESS_LEVELS.map((r) => (
                    <option key={r} value={r}>{BUSINESS_LEVEL_LABELS[r] ?? r}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-12">
                <Form.Label>Functional roles</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  {FUNCTIONAL_ROLES.map((r) => (
                    <Form.Check
                      key={r}
                      type="checkbox"
                      id={`user-functional-role-${r}`}
                      label={r}
                      checked={(form.functionalRoles ?? []).includes(r)}
                      onChange={() => toggleFunctionalRole(r)}
                    />
                  ))}
                </div>
              </Form.Group>
              {(form.businessLevel === 'Division' || form.businessLevel === 'MasterAdmin') && (
                <Form.Group className="col-md-4">
                  <Form.Label>{form.businessLevel === 'MasterAdmin' ? 'Division for PCR import' : 'Division'}</Form.Label>
                  <Form.Select
                    value={form.divisionId ?? ''}
                    onChange={(e) => setForm({ ...form, divisionId: e.target.value || undefined })}
                  >
                    <option value="">{form.businessLevel === 'MasterAdmin' ? 'No division' : 'Select'}</option>
                    {divisions.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </Form.Select>
                  {form.businessLevel === 'MasterAdmin' && (
                    <Form.Text className="text-muted">
                      Group still sees every division. PCR imports are stored in this division.
                    </Form.Text>
                  )}
                </Form.Group>
              )}
              {form.businessLevel === 'Subdivision' && (
                <Form.Group className="col-md-4">
                  <Form.Label>Subdivision</Form.Label>
                  <Form.Select
                    value={form.subdivisionId ?? ''}
                    onChange={(e) => setForm({ ...form, subdivisionId: e.target.value || undefined })}
                  >
                    <option value="">Select</option>
                    {subdivisions.map((s) => (
                      <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
              {form.businessLevel === 'Business' && (
                <Form.Group className="col-12">
                  <Form.Label>Business entities</Form.Label>
                  <div className="d-flex flex-wrap gap-2" style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {businessEntities.map((b) => (
                      <Form.Check
                        key={b.id}
                        type="checkbox"
                        id={`user-be-${b.id}`}
                        label={`${b.name} (${b.code})`}
                        checked={(form.businessEntityIds ?? []).includes(b.id)}
                        onChange={() => toggleBusinessEntity(b.id)}
                      />
                    ))}
                  </div>
                </Form.Group>
              )}
              <div className="d-flex justify-content-end gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={() => { setCreating(false); setEditingId(null) }}
                >
                  Cancel
                </Button>
                {creating ? (
                  <Button type="button" variant="primary" onClick={handleSaveCreate}>
                    Create
                  </Button>
                ) : (
                  <Button type="button" variant="primary" onClick={handleSaveEdit}>
                    Save
                  </Button>
                )}
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-muted">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="text-muted">No users. Add one to use dev login.</div>
          ) : (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Display name</th>
                  <th>Email</th>
                  <th>Password</th>
                  <th>Status</th>
                  <th>Business level</th>
                  <th>Functional roles</th>
                  <th>Division / Subdivision / BEs</th>
                  <th style={{ width: 220 }}></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.userId}</td>
                    <td>{u.displayName ?? '-'}</td>
                    <td>{u.email ?? '-'}</td>
                    <td>{u.hasPassword ? 'Set' : 'Not set'}</td>
                    <td>{u.isAutoProvisioned ? 'Pending approval' : 'Approved'}</td>
                    <td>{BUSINESS_LEVEL_LABELS[u.businessLevel ?? 'Business'] ?? u.businessLevel ?? 'Business'}</td>
                    <td>{(u.functionalRoles?.length ?? 0) > 0 ? u.functionalRoles.join(', ') : '-'}</td>
                    <td>
                      {u.divisionId && (
                        <div>Division: {divisions.find((d) => d.id === u.divisionId)?.name ?? u.divisionId}</div>
                      )}
                      {u.subdivisionId && (
                        <div>Subdivision: {subdivisions.find((s) => s.id === u.subdivisionId)?.name ?? u.subdivisionId}</div>
                      )}
                      {(u.businessEntityIds?.length ?? 0) > 0 && (
                        <div>BEs: {u.businessEntityIds!.length}</div>
                      )}
                      {!u.divisionId && !u.subdivisionId && (u.businessEntityIds?.length ?? 0) === 0 && '-'}
                    </td>
                    <td className="text-end">
                      {canImpersonate && u.userId !== currentUser?.userId && !currentUser?.isImpersonating && (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => handleImpersonate(u)}
                          title="Sign in as this user to see what they see"
                        >
                          View as
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="me-2"
                        onClick={() => handleEdit(u)}
                      >
                        Edit
                      </Button>
                      {u.isAutoProvisioned && (
                        <Button
                          size="sm"
                          variant="outline-success"
                          className="me-2"
                          onClick={() => handleApprove(u.id)}
                        >
                          Approve
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(u.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  )
}
